import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  DestroyRef,
  signal,
} from '@angular/core';
import { Data } from '@angular/router';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Sort, SortDirection } from '@angular/material/sort';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';

import { Store } from '@ngxs/store';
import { FieldDefsState } from '@state/fields/state';
import { plural } from 'pluralize';
import orderBy from 'lodash-es/orderBy';
import { FilterListItemChangeArgs, FilterListItemModel, FilterListItemValueModel, TsOverlayComponent } from 'tsui';
import { RelatedToType } from '@shared/enums';
import { NewPermission } from '@core/enums/permission';
import { FieldDef, QueryConfig } from '@core/models';
import { LookupService } from '@services/lookup.service';
import { getAllowedFields } from '@shared/functions/fields-functions';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectPagination } from 'app/modules/projects/projects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { Compset, Project, Property } from 'app/modules/projects/projects.types';
import { SelectableFieldDef } from '@shared/components/column-selector/column-selector.types';

const UI_TABLE_COLUMNS = ['index', 'selected', 'name', 'address', 'remove'];
@Component({
  selector: 'app-comps-edit',
  templateUrl: './comps-edit.component.html',
  styles: [
    `
      :host {
        .table-loading {
          filter: blur(4px);
          pointer-events: none;
        }
      }
    `,
  ],
})
export class CompsEditComponent {
  @Input() compset: Compset;
  @Input() compsetFieldDefs: FieldDef[] = [];
  @Input() selectedOrder: string[] = [];
  @Input() dataSource: MatTableDataSource<Compset>;

  @ViewChild('overlayBox') overlayBox: TsOverlayComponent;

  @Input() set displayColumns(fields: string[]) {
    if (this.isCompsetDynamic) {
      this._displayColumns = ['index', 'address', ...fields];
    } else {
      this._displayColumns = ['index', 'selected', 'address', ...fields];
    }
  }

  @Input() property: Property;
  @Input() project: Project;
  @Input() data: Data;

  @Output() onSaveColumns = new EventEmitter<SelectableFieldDef[]>();
  @Output() onSave = new EventEmitter<{ compset: Compset; data: any[] }>();
  @Output() ready = new EventEmitter<any>();
  @Output() dataSourceChange = new EventEmitter<MatTableDataSource<Compset>>();

  private pageEvent: PageEvent;
  private compsetDefinition: any;
  private queryFilters: Partial<QueryConfig.QueryFilters>;
  private fieldDefs: FieldDef[] = [];
  private fields: string[] = [];

  miles = new FormControl(10);
  isLoading = signal(false);
  selectedCompIds: number[] = [];
  pagination: ProjectPagination;
  _displayColumns: string[] = [];
  includeSelected = false;
  displayType: string;
  sortBy: string = 'dist_miles';
  sortDirection: SortDirection = 'asc';
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  filterChanged = false;
  filters: Partial<QueryConfig.QueryFilters> = { fjson: '[]', q: '' };

  private readonly destroyRef = inject(DestroyRef);

  get isCompsetDynamic(): boolean {
    return this.compset.compset_type === 'dynamic';
  }

  get isCompsetStatic(): boolean {
    return this.compset.compset_type === 'static';
  }

  constructor(
    private projectService: ProjectsService,
    private propertiesService: PropertiesService,
    private changeDetectorRef: ChangeDetectorRef,
    public readonly lookupService: LookupService,
    private store: Store,
  ) {
    this.pagination = {
      length: 1,
      size: 25,
      page: 1,
      lastPage: 1,
      startIndex: 0,
      endIndex: 0,
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.compset) {
      this.compsetDefinition = JSON.parse(this.compset.definition || '{}');
      this.queryFilters = this.compsetDefinition?.filters ?? {};
      this.filters = this.queryFilters;
      const parsedFJSON = this.fjsonStringToFilterValue(this.filters?.fjson ?? '[]');
      this.filterValueList.next(parsedFJSON);
      this.fields = this.compsetDefinition?.fields ?? [];
      this.miles.setValue(this.compsetDefinition?.miles ?? 10);
      this.sortBy = this.compsetDefinition?.sortBy ?? 'dist_miles';
      this.sortDirection = this.compsetDefinition?.sortDirection ?? 'asc';
      this.displayType = this.compset.object_type.toLowerCase() === 'project' ? 'deal' : 'property';
      this.selectedCompIds = this.compsetDefinition?.comps?.map((compId) => +compId) ?? [];
    }
  }

  ngOnInit() {
    this.pageEvent = new PageEvent();
    this.pageEvent.pageIndex = 0;
    this.pageEvent.pageSize = 25;
    this.loadDataComps();
    this.fieldDefs = getAllowedFields(this.data.fieldDefs, this.compset.object_type);

    this.typeFields.next(
      this.fieldDefs
        .map(({ label, name, type, meta }) => {
          const fieldOptions = this.formatFieldOptions(meta);
          return {
            label,
            name,
            meta: {
              filtering: { operators: meta?.filtering?.operators || [], functions: meta?.filtering?.functions || [] },
              options: fieldOptions,
              multi_select: meta?.multi_select || false,
              record_type: meta?.record_type || null,
            },
            type,
          } as FilterListItemModel;
        })
        .filter((field) => field.name !== 'dist_miles'),
    );

    this.miles.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filterChanged = true;
        this.loadDataComps();
      });
  }

  private formatFieldOptions(meta: any): string[] | { label: string; value: string }[] {
    const dropdownOptions = this.store.selectSnapshot(FieldDefsState.selectDropdownOptionsBy(meta?.option_name));

    if (dropdownOptions) {
      return dropdownOptions.map((option) => ({ label: option.name, value: option.id }));
    }

    return meta?.options || [];
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private getRequestObservable(type: RelatedToType, ids = []): Observable<any> {
    let observable: Observable<any>;
    const milesValue = this.miles.value > 0 ? this.miles.value : 1;
    const fields: string[] = (this.compsetDefinition.fields || ['address_string', 'title'])
      .filter((field: string) => !UI_TABLE_COLUMNS.includes(field))
      .concat(['latitude', 'longitude']);

    let filters: Partial<QueryConfig.QueryFilters> = {
      ...this.queryFilters,
      location: {
        near: {
          lat: parseFloat(this.property.address.latitude.toString()),
          lon: parseFloat(this.property.address.longitude.toString()),
        },
        within: `${milesValue.toString()}mi`,
      },
    };

    if (ids.length > 0) {
      filters = { fjson: JSON.stringify([['id', 'in', ids]]) };
    }

    if (!this.hasDistMilesColumn()) {
      delete filters.location;
    }

    if (type === RelatedToType.Project) {
      observable = this.projectService.exploreProjects(
        fields,
        filters,
        ids.length > 0 ? 0 : this.pageEvent.pageIndex,
        ids.length > 0 ? ids.length : this.pageEvent.pageSize,
        this.sortBy,
        this.sortDirection,
      );
    } else if (type === RelatedToType.Property) {
      observable = this.propertiesService.getProperties(
        fields,
        filters,
        ids.length > 0 ? 0 : this.pageEvent.pageIndex,
        ids.length > 0 ? ids.length : this.pageEvent.pageSize,
        this.sortBy,
        this.sortDirection,
      );
    }

    return observable;
  }

  private loadDataComps(ids = []): void {
    const apiKey = plural(this.compset.object_type).toLowerCase();
    this.isLoading.set(true);
    this.getRequestObservable(this.compset.object_type, ids)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((result) => {
          if (ids.length > 0) {
            return this.getRequestObservable(this.compset.object_type, []).pipe(
              map((allResult) => {
                const items = [...result[apiKey], ...allResult[apiKey]];
                // remove duplicate items ids
                const uniqueItems = items.filter(
                  (item, index, self) => self.findIndex((t) => t.id === item.id) === index,
                );
                allResult[apiKey] = uniqueItems;
                return allResult;
              }),
            );
          } else {
            return of(result);
          }
        }),
      )
      .subscribe((result: any) => {
        const sortedComps = orderBy(
          result[apiKey].filter((comp) => +comp.id !== +this.project.id),
          [this.sortBy],
          [this.sortDirection.toString() as 'asc' | 'desc'],
        );

        // Always apply selection state to all comps
        sortedComps.forEach((comp) => {
          comp.selected = this.selectedCompIds.includes(+comp.id);
        });

        let index = result.pagination.startIndex + 1;
        for (const comp of sortedComps) {
          comp.index = index;
          index++;
        }

        this.dataSource.data = sortedComps;
        this.dataSourceChange.emit(this.dataSource);
        this.pagination = result.pagination;
        this.isLoading.set(false);
        this.ready.emit(true);
        this.changeDetectorRef.markForCheck();
      });
  }

  onAddFilterItem(): void {
    this.filterChanged = true;
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterChanged = true;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterChanged = true;
    this.filterValueList.next(newState);
  }

  onFilterCleared(): void {
    this.filterChanged = true;
    this.filterValueList.next([]);
    this.filters = { fjson: `[]`, q: '' };
    this.queryFilters = this.filters;
    this.loadDataComps();
  }

  onFilterApplied(): void {
    this.filters = {
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
      q: '',
    };
    this.queryFilters = this.filters;
    this.loadDataComps();
    this.overlayBox.closeOverlay();
  }

  hasDistMilesColumn(): boolean {
    return this._displayColumns.includes('dist_miles');
  }

  announceSortChange(sortState: Sort): void {
    this.sortBy = sortState.active;
    this.sortDirection = sortState.direction;
    this.loadDataComps();
  }

  saveColumn(event: SelectableFieldDef[]): void {
    this.fields = event.map((field) => field.name);
    this.compsetDefinition.fields = this.fields;
    this.announceSortChange({ active: this.sortBy, direction: this.sortDirection });
    this.onSaveColumns.emit(event);
  }

  save(): void {
    const compset = { ...this.compset };
    this.compsetDefinition.comps = this.selectedCompIds;
    this.compsetDefinition.filters = this.queryFilters;
    this.compsetDefinition.fields = this.fields;
    this.compsetDefinition.sortBy = this.sortBy;
    this.compsetDefinition.miles = this.miles.value ?? 10;
    this.compsetDefinition.sortDirection = this.sortDirection;
    compset.definition = JSON.stringify(this.compsetDefinition);
    this.filterChanged = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      this.onSave.emit({ compset, data: this.dataSource.data });
    }, 300);
  }

  discard(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      this.onSave.emit(null);
    }, 300);
  }

  showSelectedComps(event: MatCheckboxChange): void {
    this.includeSelected = event.checked;
    this.loadDataComps(this.includeSelected ? this.selectedCompIds : []);
  }

  toggleSelected(comp: Compset): void {
    if (comp.selected) {
      comp.selected = false;
      const index = this.selectedCompIds.findIndex((compId) => +compId === +comp.id);
      this.selectedCompIds.splice(index, 1);
    } else {
      comp.selected = true;
      this.selectedCompIds.push(+comp.id);
    }
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef: FieldDef) => fieldDef.name == name);
  }

  getOptionName(fieldDef: FieldDef, value): string {
    if (value === null) return '';
    if (fieldDef.meta.option_name) {
      if (this.data.options[fieldDef.meta.option_name])
        return this.data.options[fieldDef.meta.option_name].find((option) => option.id == value)?.name;
    } else if (fieldDef.meta.options) {
      return fieldDef.meta.options.find((option) => option == value);
    }
    return '';
  }

  isFieldTypeAllowed(fieldDef: FieldDef): boolean {
    return !['number', 'decimal', 'percent', 'money', 'select', 'date', 'datetime'].includes(fieldDef?.type);
  }

  page(event: PageEvent): void {
    this.pageEvent = event;
    this.loadDataComps(this.includeSelected ? this.selectedCompIds : []);
  }

  getTableColumns(): string[] {
    return this._displayColumns.filter((item) => !UI_TABLE_COLUMNS.includes(item));
  }

  canEditDeal(): [NewPermission, boolean] {
    return [NewPermission.DEALS_EDIT, this.project?.permissions?.is_editable];
  }

  trackByFn(index: number, item: any): number {
    return item.id || index;
  }
}
